import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Bot,
  BookOpen,
  Boxes,
  CalendarClock,
  Check,
  CreditCard,
  FileInput,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  Globe2,
  Headphones,
  Inbox,
  Kanban,
  Languages,
  LayoutTemplate,
  Mail,
  Megaphone,
  Menu,
  MessageCircle,
  Phone,
  PhoneCall,
  Receipt,
  Repeat,
  Search,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from 'lucide-react';
import { Brand, BrandMark, PRODUCT_NAME } from '@/components/shared/brand';
import { useSession } from '@/features/auth/use-session';
import { LanguageToggle } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const NAV = [
  ['#platform', 'Platform'],
  ['#uae', 'Built for the UAE'],
  ['#pricing', 'Pricing'],
  ['#faq', 'FAQ'],
] as const;

/**
 * The signed-out front door. A clean white page in the coral-and-slate
 * identity: the product itself as the hero picture, the hubs one tab at a
 * time, what the UAE needs, prices and questions. Every feature claimed here
 * is a screen that exists in the app today.
 */
export function HomePage() {
  const { data: user } = useSession();
  const signedIn = Boolean(user);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-slate-900">
      <Header signedIn={signedIn} />
      <Hero signedIn={signedIn} />
      <Proof />
      <Platform />
      <Everything />
      <Uae />
      <Pricing signedIn={signedIn} />
      <Faq />
      <ClosingCta signedIn={signedIn} />
      <Footer />
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function Cta({ to, children, large = false, className }: { to: string; children: ReactNode; large?: boolean; className?: string }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg bg-primary font-bold text-white shadow-sm transition-colors hover:bg-primary/90',
        large ? 'h-12 px-6 text-[15px]' : 'h-10 px-4 text-sm',
        className,
      )}
    >
      {children}
    </Link>
  );
}

function Ghost({ to, children, large = false }: { to: string; children: ReactNode; large?: boolean }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg border-2 border-slate-900 font-bold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white',
        large ? 'h-12 px-6 text-[15px]' : 'h-10 px-4 text-sm',
      )}
    >
      {children}
    </Link>
  );
}

function Heading({ kicker, title, body, center = false }: { kicker: string; title: ReactNode; body?: ReactNode; center?: boolean }) {
  return (
    <div className={cn('max-w-2xl', center && 'mx-auto text-center')}>
      <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-primary">{kicker}</p>
      <h2 className="mt-3 text-[2rem] font-bold leading-[1.12] tracking-tight sm:text-[2.6rem]">{title}</h2>
      {body && <p className="mt-4 text-lg leading-relaxed text-slate-600">{body}</p>}
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────

function Header({ signedIn }: { signedIn: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
        <Link to="/" aria-label={PRODUCT_NAME}>
          <Brand size="sm" />
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map(([href, label]) => (
            <a key={href} href={href} className="rounded-md px-3 py-2 text-[15px] font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              {label}
            </a>
          ))}
        </nav>
        <div className="ms-auto hidden items-center gap-2 md:flex">
          <LanguageToggle />
          {signedIn ? (
            <Cta to="/dashboard">
              Open DovoCRM <ArrowRight className="h-4 w-4" />
            </Cta>
          ) : (
            <>
              <Link to="/login" className="rounded-md px-3 py-2 text-[15px] font-semibold text-slate-700 hover:text-slate-900">
                Log in
              </Link>
              <Cta to="/signup">Get started free</Cta>
            </>
          )}
        </div>
        <button className="ms-auto rounded-md p-2 md:hidden" onClick={() => setOpen((value) => !value)} aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          {NAV.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="block rounded-md px-2 py-2.5 font-semibold text-slate-700">
              {label}
            </a>
          ))}
          <div className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-3">
            <LanguageToggle />
            <div className="ms-auto flex gap-2">
              {signedIn ? (
                <Cta to="/dashboard">Open DovoCRM</Cta>
              ) : (
                <>
                  <Ghost to="/login">Log in</Ghost>
                  <Cta to="/signup">Start free</Cta>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

function Hero({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="relative overflow-hidden bg-[#fff6f2]">
      <div className="absolute inset-0 bg-grid opacity-70" aria-hidden />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:pb-24 lg:pt-20">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-white px-3 py-1 text-[13px] font-semibold text-slate-700">
            <span className="h-2 w-2 rounded-full bg-primary" />
            The CRM platform made for UAE businesses
          </span>
          <h1 className="mt-6 text-[2.6rem] font-bold leading-[1.05] tracking-tight sm:text-[3.6rem]">
            Grow better with one platform for <span className="text-primary">sales, marketing and service.</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
            Every WhatsApp chat, lead, deal, quote and VAT invoice in one place — in English and Arabic, priced in AED. Your team sees
            the whole customer, and nothing falls through the cracks.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {signedIn ? (
              <Cta to="/dashboard" large>
                Open your workspace <ArrowRight className="h-4 w-4" />
              </Cta>
            ) : (
              <>
                <Cta to="/signup" large>
                  Get started free <ArrowRight className="h-4 w-4" />
                </Cta>
                <Ghost to="/login" large>
                  Log in
                </Ghost>
              </>
            )}
          </div>
          <p className="mt-4 text-sm text-slate-500">14-day free trial · No credit card needed · Set up in minutes</p>
        </div>
        <ProductShot />
      </div>
    </section>
  );
}

/** The app itself, drawn small: the shell, a contact list and the assistant. */
function ProductShot() {
  const rows = [
    ['Aisha Khan', 'aisha@emaar.example', 'Emaar Hospitality', 'Qualified', '#0ea5e9'],
    ['Omar Rahman', 'omar@alnoor.example', 'Al Noor Trading', 'New', '#64748b'],
    ['Fatima Al Zaabi', 'fatima@desertrose.example', 'Desert Rose Clinics', 'Customer', '#16a34a'],
    ['Ravi Menon', 'ravi@marinatech.example', 'Marina Tech FZ-LLC', 'Proposal', '#8b5cf6'],
    ['Layla Haddad', 'layla@emaar.example', 'Emaar Hospitality', 'Qualified', '#0ea5e9'],
  ];
  return (
    <div className="relative" dir="ltr" aria-hidden>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[#18202e] shadow-brand-lg">
        <div className="flex h-10 items-center gap-2 px-3">
          <BrandMark size={20} />
          <div className="mx-auto flex h-6 w-1/2 items-center gap-1.5 rounded-full bg-white/10 px-2.5 text-[10px] text-white/50">
            <Search className="h-3 w-3" /> Search DovoCRM
          </div>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-white/80">
            <Sparkles className="h-3 w-3 text-[#ff8a6b]" /> Ask Dovo
          </span>
        </div>
        <div className="flex">
          <div className="hidden w-28 shrink-0 space-y-1 px-2 py-2 sm:block">
            {['Home', 'Inbox', 'Contacts', 'Companies', 'Deals', 'Leads'].map((item) => (
              <div
                key={item}
                className={cn('rounded px-2 py-1 text-[10px]', item === 'Contacts' ? 'bg-white/10 font-semibold text-white' : 'text-white/55')}
              >
                {item}
              </div>
            ))}
          </div>
          <div className="min-w-0 flex-1 rounded-tl-lg bg-slate-100 p-2.5">
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center gap-2 px-3 pt-3">
                <span className="text-sm font-bold">Contacts</span>
                <span className="rounded-full bg-slate-100 px-1.5 text-[9px] font-bold text-slate-500">1,284</span>
                <span className="ms-auto rounded bg-primary px-2 py-1 text-[9px] font-bold text-white">+ Add contact</span>
              </div>
              <div className="mt-2 flex gap-3 border-b border-slate-200 px-3 text-[10px] font-semibold">
                <span className="border-b-2 border-slate-900 pb-1.5">All contacts</span>
                <span className="text-slate-400">My contacts</span>
                <span className="text-slate-400">Decision makers</span>
              </div>
              <table className="w-full text-start text-[10px]">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-1.5 text-start font-bold">Name</th>
                    <th className="hidden px-2 py-1.5 text-start font-bold md:table-cell">Email</th>
                    <th className="px-2 py-1.5 text-start font-bold">Company</th>
                    <th className="px-2 py-1.5 text-start font-bold">Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([name, email, company, stage, colour]) => (
                    <tr key={name} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-semibold text-sky-800">{name}</td>
                      <td className="hidden px-2 py-1.5 text-slate-600 md:table-cell">{email}</td>
                      <td className="px-2 py-1.5 text-slate-600">{company}</td>
                      <td className="px-2 py-1.5">
                        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ backgroundColor: colour }}>
                          {stage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 -start-4 w-60 rounded-xl border border-slate-200 bg-white p-3 shadow-brand-lg sm:-start-8">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#25D366] text-white">
            <MessageCircle className="h-3.5 w-3.5" />
          </span>
          <span className="text-[12px] font-bold">New WhatsApp lead</span>
        </div>
        <p className="mt-2 text-[11px] text-slate-600">“Is the trade licence package still AED 12,500?”</p>
        <p className="mt-2 text-[10px] font-semibold text-primary">Assigned to Sara · reply within 5 min</p>
      </div>
      <div className="absolute -end-3 -top-5 hidden w-56 rounded-xl border border-slate-200 bg-white p-3 shadow-brand-lg sm:block">
        <p className="flex items-center gap-1.5 text-[12px] font-bold">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Ask Dovo
        </p>
        <p className="mt-1.5 text-[11px] text-slate-600">You have 3 deals closing this week worth AED 284,000. Start with Cloud migration.</p>
      </div>
    </div>
  );
}

function Proof() {
  const items = [
    ['One inbox', 'WhatsApp, Instagram, Messenger, Telegram, email and web chat'],
    ['5% VAT', 'Tax invoices with your TRN, totalled in AED'],
    ['English + العربية', 'Full right-to-left interface and Arabic PDFs'],
    ['Telr · N-Genius · Stripe', 'Clients pay invoices online'],
  ];
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        {items.map(([title, body]) => (
          <div key={title} className="flex items-start gap-3">
            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <div>
              <p className="font-bold">{title}</p>
              <p className="text-sm text-slate-600">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── The hubs ──────────────────────────────────────────────────────────────────

const HUBS = [
  {
    key: 'sales',
    label: 'Sales',
    colour: '#e8553f',
    title: 'Close more deals, with less busywork',
    body: 'Boards for every pipeline, sequences that follow up as you, meetings booked straight into your calendar and quotes your client signs online.',
    points: ['Deal and lead boards with drag and drop', 'Sequences and tracked email', 'Booking pages with round robin', 'Calls through your 3CX or Yeastar PBX', 'Quotes with e-signature', 'Goals, quotas and forecasts'],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    colour: '#8b5cf6',
    title: 'Turn visitors into leads, and leads into customers',
    body: 'Forms and pop-ups, landing pages, email and WhatsApp campaigns, lead ads from Meta, Google and TikTok, and attribution that shows what paid off.',
    points: ['Forms, pop-ups and landing pages', 'Email and WhatsApp campaigns', 'Segments and journeys', 'Meta, Google and TikTok lead ads', 'Website tracking and attribution', 'Social posts to Facebook and Instagram'],
  },
  {
    key: 'service',
    label: 'Service',
    colour: '#0ea5e9',
    title: 'Support customers the way they want to be supported',
    body: 'Every conversation in one shared inbox, salesbots that answer at 2 am, tickets with SLA timers, a help centre and a client portal.',
    points: ['Shared inbox with reply-time targets', 'Salesbots on every channel', 'Tickets, SLAs and contracts', 'Knowledge base and help centre', 'Client portal for projects and invoices', 'Projects, tasks and timesheets'],
  },
  {
    key: 'commerce',
    label: 'Commerce',
    colour: '#16a34a',
    title: 'Get paid faster, in dirhams',
    body: 'UAE tax invoices with 5% VAT, payment links through Telr, N-Genius or Stripe, subscriptions, expenses and a ledger that keeps itself.',
    points: ['VAT invoices with your TRN', 'Online payments: Telr, N-Genius, Stripe', 'Subscriptions and recurring billing', 'Expenses, budgets and cash forecast', 'English and Arabic PDFs', 'Finance reports'],
  },
] as const;

function Platform() {
  const [active, setActive] = useState<(typeof HUBS)[number]['key']>('sales');
  const hub = HUBS.find((item) => item.key === active) ?? HUBS[0];
  return (
    <section id="platform" className="scroll-mt-16 bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Heading
          center
          kicker="One customer platform"
          title="Everything your front office needs, connected"
          body="Sales, marketing, service and commerce share one database, so every team sees the same customer."
        />
        <div className="mx-auto mt-10 flex max-w-2xl flex-wrap justify-center gap-2 rounded-xl bg-slate-100 p-1.5">
          {HUBS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setActive(item.key)}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[15px] font-bold transition-colors',
                active === item.key ? 'bg-white shadow-sm' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.colour }} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h3 className="text-[1.8rem] font-bold leading-tight">{hub.title}</h3>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{hub.body}</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {hub.points.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-[15px]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: hub.colour }} strokeWidth={3} />
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <HubPicture hub={hub.key} colour={hub.colour} />
        </div>
      </div>
    </section>
  );
}

function HubPicture({ hub, colour }: { hub: string; colour: string }) {
  const frame = 'rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-brand';
  if (hub === 'sales') {
    const stages = [
      ['New Enquiry', '#64748b', ['Staff uniforms · AED 18k', 'Annual cleaning · AED 125k']],
      ['Qualified', '#0ea5e9', ['Cloud migration · AED 210k']],
      ['Proposal Sent', '#8b5cf6', ['Clinic onboarding · AED 36.5k', 'Website redesign · AED 27.5k']],
    ] as const;
    return (
      <div className={frame} dir="ltr" aria-hidden>
        <div className="grid grid-cols-3 gap-2.5">
          {stages.map(([name, stageColour, deals]) => (
            <div key={name} className="rounded-lg bg-slate-200/60 p-2">
              <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: stageColour }}>
                {name}
              </span>
              <div className="mt-2 space-y-1.5">
                {deals.map((deal) => (
                  <div key={deal} className="rounded-md border border-slate-200 bg-white p-2 text-[11px] font-semibold text-sky-800">
                    {deal}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (hub === 'marketing') {
    return (
      <div className={frame} aria-hidden>
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Form views', '12,480'],
            ['New leads', '1,032'],
            ['Cost per lead', 'AED 18'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[11px] text-slate-500">{label}</p>
              <p className="mt-1 text-lg font-bold">{value}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex h-32 items-end gap-1.5 rounded-lg border border-slate-200 bg-white p-3">
          {[30, 42, 38, 55, 48, 62, 70, 66, 80, 74, 88, 96].map((height, index) => (
            <span key={index} className="flex-1 rounded-t" style={{ height: `${height}%`, backgroundColor: colour, opacity: 0.35 + index * 0.05 }} />
          ))}
        </div>
      </div>
    );
  }
  if (hub === 'service') {
    return (
      <div className={frame} aria-hidden>
        {[
          ['#25D366', 'Aisha K.', 'WhatsApp', 'Is the trade licence package still AED 12,500?', '1m'],
          ['#E1306C', 'Omar R.', 'Instagram', 'Do you deliver to Abu Dhabi?', '4m'],
          ['#0ea5e9', 'Website visitor', 'Web chat', 'Can I book a demo for Thursday?', '9m'],
        ].map(([dot, name, channel, text, time]) => (
          <div key={name} className="mb-2 flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 last:mb-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: dot }}>
              {name[0]}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-[13px] font-bold">
                {name}
                <span className="text-[11px] font-medium text-slate-400">{channel}</span>
                <span className="ms-auto text-[11px] font-medium text-slate-400">{time}</span>
              </span>
              <span className="block truncate text-[12px] text-slate-600">{text}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={frame} aria-hidden>
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-[12px]">
        <div className="flex items-center justify-between">
          <span className="text-base font-bold">Tax invoice</span>
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Paid online</span>
        </div>
        <p className="mt-1 text-slate-500">INV-2026-00142 · TRN 100234567800003</p>
        <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3">
          {[
            ['Annual cleaning contract', 'AED 125,000.00'],
            ['VAT 5%', 'AED 6,250.00'],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between">
              <span className="text-slate-600">{label}</span>
              <span className="font-semibold">{value}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-100 pt-2 text-sm font-bold">
            <span>Total</span>
            <span>AED 131,250.00</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const EVERYTHING = [
  { icon: Inbox, title: 'Unified inbox' },
  { icon: Bot, title: 'Salesbots' },
  { icon: Kanban, title: 'Pipelines & boards' },
  { icon: Workflow, title: 'Digital pipeline & workflows' },
  { icon: Mail, title: 'Sequences & tracked email' },
  { icon: CalendarClock, title: 'Meetings & booking pages' },
  { icon: PhoneCall, title: 'PBX calling (3CX, Yeastar)' },
  { icon: FileText, title: 'Quotes & e-signature' },
  { icon: Receipt, title: 'VAT invoices' },
  { icon: CreditCard, title: 'Online payments' },
  { icon: Repeat, title: 'Subscriptions' },
  { icon: Headphones, title: 'Tickets & SLAs' },
  { icon: BookOpen, title: 'Knowledge base' },
  { icon: FileInput, title: 'Forms & pop-ups' },
  { icon: LayoutTemplate, title: 'Landing pages' },
  { icon: Megaphone, title: 'Lead ads & social' },
  { icon: Globe2, title: 'Website tracking' },
  { icon: FolderKanban, title: 'Projects & timesheets' },
  { icon: Boxes, title: 'Custom objects' },
  { icon: BarChart3, title: 'Dashboards & reports' },
  { icon: FileSpreadsheet, title: 'Import & export' },
  { icon: Sparkles, title: 'AI assistant' },
  { icon: Languages, title: 'Arabic & English' },
  { icon: ShieldCheck, title: 'Roles, 2FA & audit log' },
];

function Everything() {
  return (
    <section className="border-y border-slate-200 bg-slate-50 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Heading center kicker="All included" title="No add-ons to chase" body="Every plan runs on the same platform. These are all live today." />
        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {EVERYTHING.map((item) => (
            <div key={item.title} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5">
              <item.icon className="h-5 w-5 shrink-0 text-primary" />
              <span className="text-[14px] font-semibold">{item.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Uae() {
  const points = [
    ['AED & 5% VAT', 'Prices, quotes and tax invoices in dirhams with your TRN.'],
    ['Arabic, right to left', 'The whole app switches to Arabic in one click; PDFs too.'],
    ['WhatsApp first', 'The channel your customers already use, at the centre of the inbox.'],
    ['Emirates & free zones', 'Addresses, trade licences and free-zone companies handled properly.'],
    ['Dubai time', 'Working days, reminders and reports on Asia/Dubai time.'],
    ['Local payments', 'Telr and Network International N-Genius, plus Stripe.'],
  ];
  return (
    <section id="uae" className="scroll-mt-16 bg-[#18202e] py-20 text-white sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.3fr]">
        <div>
          <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#ff8a6b]">Built for the UAE</p>
          <h2 className="mt-3 text-[2rem] font-bold leading-[1.12] tracking-tight sm:text-[2.6rem]">
            Made here, for the way business works here
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/65">
            Global CRMs treat the region as an afterthought. DovoCRM starts from it.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {points.map(([title, body]) => (
            <div key={title} className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
              <p className="font-bold">{title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/60">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────

const PLANS = [
  {
    name: 'Starter',
    price: '49',
    blurb: 'For small teams getting organised.',
    features: ['Contacts, companies, leads & deals', 'Lead and deal boards', 'Unified inbox & website chat', 'Tasks & My day', 'Import & export', 'Mobile-ready app'],
  },
  {
    name: 'Professional',
    price: '129',
    blurb: 'For teams that sell every day.',
    popular: true,
    features: ['Everything in Starter', 'Salesbots & workflows', 'Sequences & tracked email', 'Meetings, calls & playbooks', 'Quotes, VAT invoices & payments', 'Forms, landing pages & campaigns'],
  },
  {
    name: 'Enterprise',
    price: '249',
    blurb: 'For companies that need control.',
    features: ['Everything in Professional', 'Help desk, SLAs & client portal', 'Custom objects', 'Custom roles & permissions', 'Document vault & e-signature', 'Audit log & dedicated onboarding'],
  },
];

function Pricing({ signedIn }: { signedIn: boolean }) {
  return (
    <section id="pricing" className="scroll-mt-16 bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Heading center kicker="Pricing" title="Simple prices, in dirhams" body="Per user, per month. Start with a 14-day free trial of everything." />
        <div className="mx-auto mt-12 grid max-w-5xl gap-5 lg:grid-cols-3">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={cn(
                'relative flex flex-col rounded-2xl border bg-white p-7',
                plan.popular ? 'border-primary shadow-brand-lg ring-1 ring-primary' : 'border-slate-200',
              )}
            >
              {plan.popular && (
                <span className="absolute -top-3 start-7 rounded-full bg-primary px-3 py-0.5 text-[12px] font-bold text-white">Most popular</span>
              )}
              <p className="text-lg font-bold">{plan.name}</p>
              <p className="mt-1 text-sm text-slate-600">{plan.blurb}</p>
              <p className="mt-6 flex items-baseline gap-1.5">
                <span className="text-sm font-semibold text-slate-500">AED</span>
                <span className="text-[2.6rem] font-bold leading-none tracking-tight">{plan.price}</span>
                <span className="text-sm text-slate-500">/ user / month</span>
              </p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-[14px]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={3} />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                {plan.popular ? (
                  <Cta to={signedIn ? '/admin/billing' : '/signup'} className="w-full">
                    {signedIn ? 'Choose plan' : 'Start free trial'}
                  </Cta>
                ) : (
                  <Link
                    to={signedIn ? '/admin/billing' : '/signup'}
                    className="flex h-10 w-full items-center justify-center rounded-lg border-2 border-slate-900 text-sm font-bold transition-colors hover:bg-slate-900 hover:text-white"
                  >
                    {signedIn ? 'Choose plan' : 'Start free trial'}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">Prices exclude VAT.</p>
      </div>
    </section>
  );
}

const FAQS = [
  ['How long is the free trial?', 'Fourteen days with every feature on. No card is needed to start, and your data stays if you subscribe.'],
  ['Can I bring my existing customers?', 'Yes. Import an Excel or CSV file — columns are matched for you, duplicates are caught, and an import can be undone in one click.'],
  ['Does DovoCRM issue UAE tax invoices?', 'Yes. Invoices carry your TRN, show 5% VAT line by line and total in AED, and can be shared as a link your client can pay from.'],
  ['Do I need the WhatsApp Business API?', 'To chat from inside DovoCRM you connect a WhatsApp Business API number. We help you set it up as part of onboarding.'],
  ['Does it work in Arabic?', 'Yes. Switch the interface to Arabic from the header; it turns right to left, and quotes and invoices print in Arabic too.'],
  ['Is my data secure?', 'Each company has its own isolated workspace. Passwords are hashed with Argon2, two-factor authentication is available and every change is recorded in an audit log.'],
];

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="scroll-mt-16 border-t border-slate-200 bg-slate-50 py-20 sm:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Heading center kicker="Questions" title="Frequently asked questions" />
        <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
          {FAQS.map(([question, answer], index) => (
            <div key={question}>
              <button
                type="button"
                onClick={() => setOpen(open === index ? null : index)}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-start text-[16px] font-bold"
                aria-expanded={open === index}
              >
                {question}
                <span className={cn('text-2xl font-normal leading-none text-primary transition-transform', open === index && 'rotate-45')}>+</span>
              </button>
              {open === index && <p className="-mt-1 px-6 pb-5 leading-relaxed text-slate-600">{answer}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="bg-white px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl bg-primary px-8 py-14 text-center text-white sm:px-14">
        <h2 className="text-[2rem] font-bold leading-tight tracking-tight sm:text-[2.6rem]">Ready to grow better?</h2>
        <p className="mx-auto mt-3 max-w-xl text-lg text-white/85">Set up your workspace in minutes. Your team will wonder how they worked without it.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to={signedIn ? '/dashboard' : '/signup'}
            className="inline-flex h-12 items-center gap-2 rounded-lg bg-white px-6 text-[15px] font-bold text-slate-900 transition-colors hover:bg-slate-100"
          >
            {signedIn ? 'Open DovoCRM' : 'Get started free'} <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="mailto:hello@dovocrm.com"
            className="inline-flex h-12 items-center gap-2 rounded-lg border-2 border-white/70 px-6 text-[15px] font-bold text-white transition-colors hover:bg-white/10"
          >
            <Phone className="h-4 w-4" /> Talk to us
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white py-10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 px-4 sm:flex-row sm:px-6">
        <Brand size="sm" />
        <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
          {NAV.map(([href, label]) => (
            <a key={href} href={href} className="hover:text-slate-900">
              {label}
            </a>
          ))}
          <Link to="/terms" className="hover:text-slate-900">
            Terms
          </Link>
          <Link to="/privacy" className="hover:text-slate-900">
            Privacy
          </Link>
          <a href="mailto:hello@dovocrm.com" className="hover:text-slate-900">
            hello@dovocrm.com
          </a>
        </nav>
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} {PRODUCT_NAME}. Made in the UAE.
        </p>
      </div>
    </footer>
  );
}
